import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { DateTimeHelper } from './shared/helpers/datetime.helper';
import { HubService } from './shared/services/hub.service';
import { AppState } from './store/states/app.state';
import { share } from 'rxjs/operators';
import { PushMessageService } from './shared/services/push-message.service';
import { componentDestroyed, untilComponentDestroyed } from 'src/app/shared/helpers/rxjs.operator';
import { USER_KEY } from 'src/app/shared/helpers/constants';
import { SessionStorageService } from './shared/services/session-storage.service';
import { PersonDto, PresenceType } from './api/models';
import { Subscription, partition } from 'rxjs';
import { LocalStorageService } from './shared/services/local-storage.service';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { CookieService } from 'ngx-cookie-service';

import * as fromStaticDataReasonActions from './store/actions/static-data.action';
import * as fromUserActions from './store/actions/user.action';
import * as fromUserSelectors from './store/selectors/user.selector';
import * as fromStaticDataSelectors from './store/selectors/static-data.selector';
import { CustomTranslationService } from './shared/translation/custom-translation.service';
import { SwUpdate } from '@angular/service-worker';

const COOKIE_CONSENT_NAME = 'cookie_consent';
const COOKIE_CONSENT_EXPIRY = 90; // in days

@Component({
    selector: 'diana-root',
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private hubService: HubService,
    private store$: Store<AppState>,
    private pushMessageService: PushMessageService,
    private session: SessionStorageService,
    public translationService: CustomTranslationService,
    private localStorageService: LocalStorageService,
    private cookieConsentService: NgcCookieConsentService,
    private cookieService: CookieService,
    private swUpdate: SwUpdate
  ) { 
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          document.location.reload(); // Refresh page
        }
      });
    }
  }

  ngOnInit() {
    DateTimeHelper.setLocale('de', 'Kein gültiges Datum');
    this.store$.dispatch(fromStaticDataReasonActions.loadVersion());
    this.hubService.init();
    this.hubService.connect();

    const [user$, noUser$] = partition(this.store$.select(fromUserSelectors.getCurrentUser).pipe(share()), p => !!p);
    noUser$.pipe(
      untilComponentDestroyed(this)
    ).subscribe(_ => {
      const user = JSON.parse(<any>this.session.get(USER_KEY)) as PersonDto;
      if (user) {
        this.store$.dispatch(fromUserActions.setCurrentUser({ user }));
        this.fetchStaticDataInitPush(user);
      }
    });

    // fetching language saved in localstorage
    let language = this.localStorageService.getLanguage();
    // fetching browser language and setting it
    if (!language) {
      let userLang = navigator.language;
      if (userLang && userLang.includes('-')) {
        userLang = userLang.split('-')[0];
      }
      language = this.translationService.mapLangToLanguage(userLang);
    }
    this.translationService.changeLoaderLoadLang(language);
    user$.subscribe(user => {
      if (user) {
        this.fetchStaticDataInitPush(user);
      }
    });

    // cookie consent 
    this.cookieConsentService.destroy();
    let statusSub: Subscription;
    this.translationService.isLangReady$.subscribe(isReady => {
      if (isReady && !this.cookieService.check(COOKIE_CONSENT_NAME)) {
        if (this.cookieConsentService.isOpen()) {
          this.cookieConsentService.fadeOut();
          this.cookieConsentService.destroy();
          if (statusSub) {
            statusSub.unsubscribe();
          }
        }
        let config = this.cookieConsentService.getConfig();
        config.content!.message = this.translationService.instant('general.cookie.consent.text');
        config.content!.link = this.translationService.instant('general.cookie.consent.link');
        config.content!.href = this.translationService.instant('general.cookie.consent.href');
        this.cookieConsentService.init(config);
        // status change event is triggered when pop up is closed
        statusSub = this.cookieConsentService.statusChange$.subscribe(
          () => {
            this.cookieService.set(COOKIE_CONSENT_NAME, true.toString(), COOKIE_CONSENT_EXPIRY);
          });
      }
    });
  }

  ngOnDestroy(): void {
    componentDestroyed(this);
    this.hubService.disconnect();
    this.pushMessageService.disconnect();
  }

  @HostListener('document:visibilitychange')
  visibilitychange() {
    if (!document.hidden) {
      this.hubService.registerWebClient();
    }
  }

  private fetchStaticDataInitPush(user: PersonDto) {
    this.pushMessageService.initConnection(user.currentTenant ?? 0);
    this.store$.dispatch(fromStaticDataReasonActions.loadStaticData());
    this.store$.pipe(select(fromStaticDataSelectors.getPresenceTypes), untilComponentDestroyed(this))
      .subscribe((presenceTypes: PresenceType[]) => {
        const styles = presenceTypes.map((presenceType) =>
          `.presence-custom-color-${presenceType.name.replaceAll(' ', '')} { background : ${presenceType.color};}`
        )
        var style = document.createElement('style');
        style.innerHTML = styles.join(' ');
        document.getElementsByTagName('head')[0].appendChild(style);
      });
  }
}
